import type { SxProps, Theme, TypographyProps } from '@mui/material';
import { Typography } from '@mui/material';
import { alpha, decomposeColor } from '@mui/material/styles';

interface StrikethroughTextProps {
  children: React.ReactNode;
  variant?: TypographyProps['variant'];
  sx?: SxProps<Theme>;
}

/**
 * A translucent (e.g. `text.disabled`) strikethrough — used for a struck-out
 * original price beside/above a discounted one. Text and its own line-through
 * are each rendered fully opaque, so combined they're one flat, seamless
 * shape, then faded as a single layer via `opacity` matching the color's own
 * alpha. Rendering both pieces at the color's native alpha instead would have
 * the browser blend the line and the glyphs it crosses separately, doubling
 * the overlap where they cross and reading as muddy.
 */
export function StrikethroughText({
  children,
  variant = 'body3',
  sx,
}: StrikethroughTextProps) {
  const extraSx = Array.isArray(sx) ? sx : [sx];

  return (
    <Typography
      variant={variant}
      sx={[
        (theme) => {
          const disabledAlpha =
            decomposeColor(theme.palette.text.disabled).values[3] ?? 1;
          const solid = alpha(theme.palette.text.disabled, 1);
          return {
            color: solid,
            textDecorationLine: 'line-through',
            textDecorationThickness: 'from-font',
            textDecorationSkipInk: 'none',
            textDecorationColor: solid,
            opacity: disabledAlpha,
          };
        },
        ...extraSx,
      ]}
    >
      {children}
    </Typography>
  );
}
